export interface PasswordRequirement {
  id: string;
  label: string;
  valid: boolean;
}

export interface PasswordValidationResult {
  isValid: boolean;
  score: number; // 0 to 5
  strengthLabel: 'Very Weak' | 'Weak' | 'Fair' | 'Strong' | 'Very Strong';
  requirements: PasswordRequirement[];
  error?: string;
}

export function validatePassword(password: string): PasswordValidationResult {
  const minLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);

  const requirements: PasswordRequirement[] = [
    { id: 'length', label: 'At least 8 characters', valid: minLength },
    { id: 'upper', label: 'At least 1 uppercase letter (A-Z)', valid: hasUpper },
    { id: 'lower', label: 'At least 1 lowercase letter (a-z)', valid: hasLower },
    { id: 'digit', label: 'At least 1 number (0-9)', valid: hasDigit },
    { id: 'special', label: 'At least 1 special character (!@#$%...)', valid: hasSpecial },
  ];

  const validCount = requirements.filter((r) => r.valid).length;
  const isValid = validCount === requirements.length;

  let strengthLabel: PasswordValidationResult['strengthLabel'] = 'Very Weak';
  if (validCount <= 1) strengthLabel = 'Very Weak';
  else if (validCount === 2) strengthLabel = 'Weak';
  else if (validCount === 3) strengthLabel = 'Fair';
  else if (validCount === 4) strengthLabel = 'Strong';
  else if (validCount === 5) strengthLabel = 'Very Strong';

  let error: string | undefined;
  if (!password) {
    error = 'Password is required';
  } else if (!minLength) {
    error = 'Password must be at least 8 characters';
  } else if (!hasUpper) {
    error = 'Password must include an uppercase letter';
  } else if (!hasLower) {
    error = 'Password must include a lowercase letter';
  } else if (!hasDigit) {
    error = 'Password must include a number';
  } else if (!hasSpecial) {
    error = 'Password must include a special character';
  }

  return {
    isValid,
    score: validCount,
    strengthLabel,
    requirements,
    error,
  };
}
